import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, Typography, CircularProgress,
} from '@mui/material';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { LeaveTypeService } from '../../services/leave-type.service';
import { LeaveEncashmentService } from '../../services/leaveEncashment.service';
import { ICreateLeaveEncashmentRequest, ILeaveBalanceDetail } from '../../interfaces/payroll.interface';
import { LeaveTypeConfig } from '../../interfaces/leave.interface';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: ICreateLeaveEncashmentRequest) => void;
    saving?: boolean;
    institutionId: string;
}

// Create dialog (spec §3): employee picker, leave-type select filtered to encashable+active+paid
// types, days field with the H8 `/remaining` endpoint's live hint (refetched whenever the
// employee or leave type changes), target month/year. Bound checks the backend also enforces
// (days positive, year/month range) gate submit so a malformed request never leaves the client;
// every arithmetic/existence refusal (balance exceeded, no basic component, past target month,
// cross-fiscal-year target...) stays server-side and surfaces verbatim via showApiError in the
// parent list.
const LeaveEncashmentFormDialog: React.FC<Props> = ({ open, onClose, onSave, saving = false, institutionId }) => {
    const now = new Date();
    const [employee, setEmployee] = useState<EmployeeOption | null>(null);
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [leaveTypeId, setLeaveTypeId] = useState('');
    const [days, setDays] = useState<number>(1);
    const [targetYear, setTargetYear] = useState(now.getFullYear());
    const [targetMonth, setTargetMonth] = useState(now.getMonth() + 1);
    const [remaining, setRemaining] = useState<ILeaveBalanceDetail | null>(null);
    const [remainingLoading, setRemainingLoading] = useState(false);
    const [remainingFailed, setRemainingFailed] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEmployee(null);
        setLeaveTypeId('');
        setDays(1);
        setTargetYear(now.getFullYear());
        setTargetMonth(now.getMonth() + 1);
        setRemaining(null);
        setRemainingFailed(false);
        LeaveTypeService.getByInstitution(true)
            .then((types) => setLeaveTypes(types.filter((t) => t.encashable && t.isPaid !== false)))
            .catch(() => setLeaveTypes([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Live remaining hint (spec §2.1 H8) — refetch whenever the employee or leave type changes.
    useEffect(() => {
        if (!employee || !leaveTypeId) {
            setRemaining(null);
            setRemainingFailed(false);
            return;
        }
        let cancelled = false;
        setRemainingLoading(true);
        setRemainingFailed(false);
        LeaveEncashmentService.remaining(institutionId, employee.id, leaveTypeId)
            .then((data) => {
                if (!cancelled) setRemaining(data);
            })
            .catch(() => {
                if (!cancelled) {
                    setRemaining(null);
                    setRemainingFailed(true);
                }
            })
            .finally(() => {
                if (!cancelled) setRemainingLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [employee, leaveTypeId, institutionId]);

    const isValid = !!employee && !!leaveTypeId
        && Number.isFinite(days) && days > 0
        && Number.isFinite(targetYear) && targetYear >= 2020 && targetYear <= 2100;

    const createWhy = saving ? 'Saving is in progress.'
        : !employee ? 'Choose an employee first.'
        : !leaveTypeId ? 'Choose a leave type first.'
        : !(Number.isFinite(days) && days > 0) ? 'Enter a number of days above 0 first.'
        : !isValid ? 'Enter a target year from 2020 to 2100.' : null;

    const handleSubmit = () => {
        if (!isValid || !employee) return;
        onSave({ userId: employee.id, leaveTypeId, days, targetYear, targetMonth });
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.encashForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>New Leave Encashment</DialogTitle>
            <DialogContent data-help-section="payroll.encashForm.section.details">
                <Box sx={{ mt: 1, mb: 2 }}>
                    <EmployeeAutocomplete
                        value={employee}
                        onChange={setEmployee}
                        data-help="payroll.encashForm.field.employee" label="Employee"
                        required
                        fullWidth
                    />
                </Box>
                <SearchableSelect
                    data-help="payroll.encashForm.field.leaveType" label="Leave Type"
                    options={leaveTypes}
                    value={leaveTypeId}
                    onChange={id => setLeaveTypeId(id)}
                    getOptionLabel={lt => lt.name}
                    noOptionsText="No encashable leave types"
                    sx={{ mb: leaveTypes.length === 0 ? 1 : 2 }}
                />
                {leaveTypes.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No leave type is marked encashable — enable it under Leave Types.
                    </Typography>
                )}

                {employee && leaveTypeId && (
                    <Box sx={{ mb: 2 }}>
                        {remainingLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">Loading balance…</Typography>
                            </Box>
                        )}
                        {!remainingLoading && remainingFailed && (
                            <Typography variant="body2" color="error">Failed to load remaining balance</Typography>
                        )}
                        {!remainingLoading && remaining && (
                            <Typography variant="body2" color="text.secondary">
                                Allocated {remaining.allocated} · Availed {remaining.availed} · Encashed {remaining.encashedDays} · Settled {remaining.settlementDays} · Remaining <strong>{remaining.remaining}</strong> (FY {remaining.fiscalYear})
                            </Typography>
                        )}
                    </Box>
                )}

                <TextField
                    margin="dense"
                    data-help="payroll.encashForm.field.days" label="Days"
                    type="number"
                    fullWidth
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="leave-encashment-month-label">Target Month</InputLabel>
                    <Select
                        data-help="payroll.encashForm.field.month" labelId="leave-encashment-month-label"
                        label="Target Month"
                        value={targetMonth}
                        onChange={(e) => setTargetMonth(Number(e.target.value))}
                    >
                        {MONTHS.map((label, idx) => (
                            <MenuItem key={label} value={idx + 1}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    margin="dense"
                    data-help="payroll.encashForm.field.year" label="Target Year"
                    type="number"
                    fullWidth
                    value={targetYear}
                    onChange={(e) => setTargetYear(Number(e.target.value))}
                    inputProps={{ min: 2020, max: 2100 }}
                />
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.encashForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="payroll.encashForm.save" {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)} onClick={handleSubmit} variant="contained" disabled={!isValid || saving}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LeaveEncashmentFormDialog;
